import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import innerStyles from './styles/outline-provider-hero.inner.pcss?inline';
import globalStyles from './styles/outline-provider-hero.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { SlotHelper } from '../../controllers/slot-helper';
import '../outline-container/outline-container';
import '../outline-cta/outline-cta';
import '../outline-icon/outline-icon';
import fallbackImage from '../../assets/images/provider-fallback-image.png';
import { getIsReferrerSearchListing } from '../../utilities/is-referrer-search-listing';

/**
 * The OutlineProviderHero component
 * @element outline-provider-hero
 */
@customElement('outline-provider-hero')
export class OutlineProviderHero extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerStyles,
    globalCSS: globalStyles,
  });

  private _slotHelper = new SlotHelper(this);

  @property({ type: Boolean, reflect: true })
  hasPhoto: boolean;

  @property({ type: Boolean, reflect: true })
  hasReviews: boolean;

  @property({ type: Boolean, reflect: true })
  hasProviderGroupLabel: boolean;

  @property({ type: Boolean, reflect: true })
  hasProviderGroupLogo: boolean;

  @property({ type: Boolean, reflect: true })
  hasHospitalAffiliationLabel: boolean;

  @property({ type: Boolean, reflect: true })
  hasHospitalAffiliationLogo: boolean;

  @property({ type: Boolean, reflect: true })
  hasHospitalAffiliationList: boolean;

  @property({ type: Boolean, reflect: true })
  hasBookAppointmentCard: boolean;

  @state()
  isReferrerSearchListing = false;

  connectedCallback() {
    super.connectedCallback();

    this.isReferrerSearchListing = getIsReferrerSearchListing();
  }

  firstUpdated() {
    this.hasPhoto = this._slotHelper.isNotEmpty('photo');
    this.hasReviews = this._slotHelper.isNotEmpty('reviews');
    this.hasProviderGroupLabel = this._slotHelper.isNotEmpty(
      'provider-group-label'
    );
    this.hasProviderGroupLogo = this._slotHelper.isNotEmpty(
      'provider-group-logo'
    );
    this.hasHospitalAffiliationLabel = this._slotHelper.isNotEmpty(
      'hospital-affiliation-label'
    );
    this.hasHospitalAffiliationLogo = this._slotHelper.isNotEmpty(
      'hospital-affiliation-logo'
    );
    this.hasHospitalAffiliationList = this._slotHelper.isNotEmpty(
      'hospital-affiliation-list'
    );
    this.hasBookAppointmentCard = this._slotHelper.isNotEmpty(
      'book-appointment-card'
    );
  }

  render(): TemplateResult {
    const hasFooterContent =
      this.hasProviderGroupLabel ||
      this.hasProviderGroupLogo ||
      this.hasHospitalAffiliationLabel ||
      this.hasHospitalAffiliationLogo ||
      this.hasHospitalAffiliationList;

    return html`
      <outline-container y-padding="space-m">
        ${this.isReferrerSearchListing
          ? html`
              <div class="back-to-search print-hide">
                <outline-cta variant="link">
                  <a href="${document.referrer}">
                    <outline-icon
                      icon-classes="fa-solid fa-arrow-left"
                    ></outline-icon>
                    <span>Return to search results</span>
                  </a>
                </outline-cta>
                <div class="desktop-share-options print-hide">
                  <a href="#" onclick="window.print()"
                    ><outline-icon
                      icon-classes="fa-regular fa-print fa-border"
                      size="20px"
                      color="#00449D"
                      style="--fa-border-color: #00449D;--fa-border-padding: 12px;--fa-border-radius: 4px"
                    ></outline-icon
                  ></a>
                </div>
              </div>
            `
          : null}
        <div
          class=${`inner-layout ${this.hasBookAppointmentCard ? 'display-book-appointment-widget' : ''}`}
        >
          <div class="column">
            <div class="provider-photo">
              ${this.hasPhoto
                ? html`<slot name="photo"></slot>`
                : html` <img src="${fallbackImage}" alt="" /> `}
            </div>
            <div class="content">
              <div class="provider-name">
                <slot name="name"></slot>
              </div>
              <div class="provider-specialty-container">
                <slot name="specialty"></slot>
              </div>

              <div class="icon-detail">
                <slot name="icon-detail"></slot>
              </div>

              ${this.hasReviews
                ? html`<div class="reviews print-hide">
                    <slot name="reviews"></slot>
                    <slot name="reviews-link"></slot>
                  </div>`
                : null}
            </div>
            ${hasFooterContent
              ? html`
                  <div class="footer">
                    ${this.hasProviderGroupLogo
                      ? html`<div class="provider-group">
                          <slot name="provider-group-logo"></slot>
                        </div>`
                      : null}
                    ${this.hasHospitalAffiliationList
                      ? html`<div class="hospital-affiliation-list">
                          <div>
                            <slot name="hospital-affiliation-list"></slot>
                          </div>
                        </div>`
                      : null}
                  </div>
                `
              : null}
          </div>
          <div class="mobile-share-options">
            <a href="#" onclick="window.print()"
              ><outline-icon
                icon-classes="fa-regular fa-print fa-border"
                size="20px"
                color="#00449D"
                style="--fa-border-color: #00449D;--fa-border-padding: 12px;--fa-border-radius: 4px"
              ></outline-icon
            ></a>
          </div>
          ${this.hasBookAppointmentCard
            ? html` <slot name="book-appointment-card"></slot> `
            : null}
        </div>
      </outline-container>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-provider-hero': OutlineProviderHero;
  }
}
